import React from 'react';
import { Mail, Calendar, Shield, CheckCircle, XCircle, Lock, AlertCircle, ToggleLeft, ToggleRight, Settings, Power, Trash2, Edit, UserCog, CalendarClock, CalendarX } from 'lucide-react';
import { Professional } from '../types';
import { useRoleProtection } from '../hooks/useRoleProtection';
import { useAuth } from '../hooks/useAuth';
import { useBusiness } from '../contexts/BusinessContext';
import { isBusinessOwnerForBusiness } from '../utils/roleUtils';
import ProfessionalWorkScheduleModal from './ProfessionalWorkScheduleModal';
import ProfessionalTimeOffModal from './ProfessionalTimeOffModal';

interface ProfessionalCardProps {
  professional: Professional;
  onToggleActive: (professionalId: string, isActive: boolean) => void;
  onToggleServicePermission: (professionalId: string, canPerformServices: boolean) => void;
  onDelete: (professionalId: string, professionalName: string) => void;
  onEdit: (professional: Professional) => void;
}

const ProfessionalCard: React.FC<ProfessionalCardProps> = ({
  professional,
  onToggleActive,
  onToggleServicePermission,
  onDelete,
  onEdit
}) => {
  const { canPerformAction, getRoleProtectionStatus } = useRoleProtection();
  const { user } = useAuth();
  const { currentBusiness } = useBusiness();

  // Check if current user is a Business Owner for this business
  const isCurrentUserBusinessOwner = isBusinessOwnerForBusiness(user, currentBusiness?.id || '');
  
  // Check if viewing own card and is Business Owner
  const isViewingOwnCard = user?.id === professional.id;
  const isOwnOwnerCard = isViewingOwnCard && professional.role === 'Business Owner' && isCurrentUserBusinessOwner;
  
  // Show toggle button if current user is Business Owner and professional is active
  // (doesn't make sense to toggle services for inactive personnel)
  const showCanPerformServicesToggle = isCurrentUserBusinessOwner && professional.isActive;

  // Get role protection status for this professional
  const roleProtection = professional.roleProtection || getRoleProtectionStatus(professional.id);

  const getInitials = (name: string): string => {
    return name.split(' ').map((n: string) => n[0]).join('').toUpperCase();
  };

  const formatDate = (dateString: string): string => {
    if (!dateString) return 'Date not available';
    
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Date not available';
    
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };


  const getRoleBadgeColor = (role?: string) => {
    switch (role) {
      case 'Business Owner':
        return 'bg-yellow-100 text-yellow-800';
      case 'Professional':
        return 'bg-blue-100 text-blue-800';
      case 'Manager':
        return 'bg-purple-100 text-purple-800';
      case 'Assistant':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Check if buttons should be disabled
  const canEdit = canPerformAction('edit', professional.id);
  const canDeactivate = canPerformAction('deactivate', professional.id);
  const canDelete = canPerformAction('delete', professional.id);

  // Get tooltip text for disabled buttons
  const getTooltipText = (action: string): string => {
    if (roleProtection.isProtected) {
      return roleProtection.reason || `${action} is not allowed for this role`;
    }
    return `${action} this professional`;
  };

  // Local dropdown state for gear menu
  const [actionsOpen, setActionsOpen] = React.useState(false);
  const [isWorkScheduleOpen, setIsWorkScheduleOpen] = React.useState(false);
  const [isTimeOffOpen, setIsTimeOffOpen] = React.useState(false);

  React.useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.professional-actions-dropdown-container')) {
        setActionsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className={`rounded-lg shadow-sm border border-gray-200 overflow-visible hover:shadow-md transition-shadow duration-200 flex flex-col relative ${
      !professional.isActive 
        ? 'bg-gray-50 border-gray-200' 
        : 'bg-white border-gray-200'
    }`}>
      {/* Gear icon in top right corner - positioned outside grayed content */}
      <div className="absolute top-3 right-3 z-10 professional-actions-dropdown-container">
        <button
          type="button"
          onClick={() => setActionsOpen(o => !o)}
          className={`p-1.5 rounded transition-colors ${actionsOpen ? 'bg-gray-100 text-gray-700' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-50'}`}
          title={(!canEdit && !showCanPerformServicesToggle && !canDeactivate && !canDelete) ? 'No actions available' : 'More actions'}
        >
          <Settings className="w-4 h-4" />
        </button>
        {actionsOpen && (
          <div className="absolute right-0 top-full mt-1 w-52 bg-white border border-gray-200 rounded-lg shadow-xl z-[99999]">
            <div className="py-1">
              {/* For Business Owner role, only show Enable/Disable Services */}
              {professional.role === 'Business Owner' ? (
                showCanPerformServicesToggle ? (
                  <button
                    onClick={() => {
                      onToggleServicePermission(professional.id, !professional.canPerformServices);
                      setActionsOpen(false);
                    }}
                    className={`w-full text-left px-4 py-2 text-sm transition-colors flex items-center gap-2 ${
                      professional.canPerformServices
                        ? 'text-orange-600 hover:text-orange-700 hover:bg-orange-50'
                        : 'text-gray-600 hover:text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {professional.canPerformServices ? (
                      <ToggleRight className="w-4 h-4" />
                    ) : (
                      <ToggleLeft className="w-4 h-4" />
                    )}
                    {professional.canPerformServices ? 'Disable Services' : 'Enable Services'}
                  </button>
                ) : (
                  <div className="px-4 py-2 text-xs text-gray-500">
                    {roleProtection.isProtected ? roleProtection.reason : 'No actions available'}
                  </div>
                )
              ) : (
                <>
                  {canEdit && (
                    <button
                      onClick={() => {
                        onEdit(professional);
                        setActionsOpen(false);
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-gray-900 hover:bg-gray-50 transition-colors flex items-center gap-2"
                    >
                      <Edit className="w-4 h-4" />
                      Edit
                    </button>
                  )}
                  {isCurrentUserBusinessOwner && (
                    <>
                      <button
                        onClick={() => {
                          setIsWorkScheduleOpen(true);
                          setActionsOpen(false);
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-gray-900 hover:bg-gray-50 transition-colors flex items-center gap-2"
                      >
                        <CalendarClock className="w-4 h-4" />
                        Work Schedule
                      </button>
                      <button
                        onClick={() => {
                          setIsTimeOffOpen(true);
                          setActionsOpen(false);
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-gray-900 hover:bg-gray-50 transition-colors flex items-center gap-2"
                      >
                        <CalendarX className="w-4 h-4" />
                        Time Off
                      </button>
                      <div className="border-t border-gray-200 my-1"></div>
                    </>
                  )}
                  {showCanPerformServicesToggle && (
                    <button
                      onClick={() => {
                        onToggleServicePermission(professional.id, !professional.canPerformServices);
                        setActionsOpen(false);
                      }}
                      className={`w-full text-left px-4 py-2 text-sm transition-colors flex items-center gap-2 ${
                        professional.canPerformServices
                          ? 'text-orange-600 hover:text-orange-700 hover:bg-orange-50'
                          : 'text-gray-600 hover:text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      {professional.canPerformServices ? (
                        <ToggleRight className="w-4 h-4" />
                      ) : (
                        <ToggleLeft className="w-4 h-4" />
                      )}
                      {professional.canPerformServices ? 'Disable Services' : 'Enable Services'}
                    </button>
                  )}
                  {canDeactivate && (
                    <button
                      onClick={() => {
                        onToggleActive(professional.id, !professional.isActive);
                        setActionsOpen(false);
                      }}
                      className={`w-full text-left px-4 py-2 text-sm transition-colors flex items-center gap-2 ${
                        professional.isActive
                          ? 'text-orange-600 hover:text-orange-700 hover:bg-orange-50'
                          : 'text-green-600 hover:text-green-700 hover:bg-green-50'
                      }`}
                    >
                      <Power className="w-4 h-4" />
                      {professional.isActive ? 'Disable' : 'Enable'}
                    </button>
                  )}
                  {canDelete && (
                    <button
                      onClick={() => {
                        onDelete(professional.id, professional.name);
                        setActionsOpen(false);
                      }}
                      className="w-full text-left px-4 py-2 text-sm transition-colors text-red-600 hover:text-red-700 hover:bg-red-50 flex items-center gap-2"
                    >
                      <Trash2 className="w-4 h-4" />
                      Remove
                    </button>
                  )}
                  {!canEdit && !showCanPerformServicesToggle && !canDeactivate && !canDelete && (
                    <div className="px-4 py-2 text-xs text-gray-500">
                      {roleProtection.isProtected ? roleProtection.reason : 'No actions available'}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>
      {/* Professional Header */}
      <div className={`p-4 border-b border-gray-100 ${!professional.isActive ? 'opacity-40 grayscale' : ''}`}>
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3 flex-1">
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
              <span className="text-blue-600 font-semibold text-sm">
                {getInitials(professional.name)}
              </span>
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="text-lg font-semibold text-gray-900 break-words">
                  {professional.name}
                </h3>
                {roleProtection.isProtected && (
                  <Lock className="h-4 w-4 text-orange-500" />
                )}
              </div>
              <div className="flex items-center gap-2">
                {professional.role && (
                  <span className={`inline-block px-2 py-1 text-xs font-medium rounded-full ${getRoleBadgeColor(professional.role)}`}>
                    {professional.role}
                  </span>
                )}
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                  professional.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                }`}>
                  {professional.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Professional Details */}
      <div className={`p-4 flex-1 ${!professional.isActive ? 'opacity-40 grayscale' : ''}`}>
        <div className="space-y-3 mb-4">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Mail className="h-4 w-4 text-gray-400" />
            <span className="line-clamp-1">{professional.email}</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Calendar className="h-4 w-4 text-gray-400" />
            <span>Joined {formatDate(professional.joinedAt)}</span>
          </div>
          {isOwnOwnerCard && (
            <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start gap-2">
                <Lock className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <div className="text-sm font-semibold text-blue-900 mb-1">
                    Your owner role is fixed
                  </div>
                  <div className="text-xs text-blue-800">
                    You're set as the Business Owner for this business and your role can't be changed.
                  </div>
                </div>
              </div>
            </div>
          )}
          <div className="mt-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
            <div className="flex items-start gap-2">
              <UserCog className="h-4 w-4 text-gray-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <div className="text-sm font-semibold text-gray-900 mb-1">
                  Service permissions
                </div>
                <div className="flex items-center gap-2 text-xs mb-2">
                  {professional.canPerformServices ? (
                    <CheckCircle className="h-3.5 w-3.5 text-emerald-500" />
                  ) : (
                    <XCircle className="h-3.5 w-3.5 text-orange-500" />
                  )}
                  <span className={`font-medium ${
                    professional.canPerformServices ? 'text-emerald-700' : 'text-orange-700'
                  }`}>
                    {professional.canPerformServices ? 'Authorized to perform services' : 'Not authorized to perform services'}
                  </span>
                </div>
                {!professional.canPerformServices && (
                  <div className="text-xs text-gray-700">
                    Turn on <span className="font-semibold">Enable services</span> if you also perform services for customers.
                  </div>
                )}
              </div>
            </div>
          </div>
          
        </div>
      </div>

      <ProfessionalWorkScheduleModal
        isOpen={isWorkScheduleOpen}
        onClose={() => setIsWorkScheduleOpen(false)}
        businessId={currentBusiness?.id || ''}
        professional={{ id: professional.id, name: professional.name }}
        canManage={isCurrentUserBusinessOwner}
      />

      <ProfessionalTimeOffModal
        isOpen={isTimeOffOpen}
        onClose={() => setIsTimeOffOpen(false)}
        businessId={currentBusiness?.id || ''}
        professional={{ id: professional.id, name: professional.name }}
        canManage={isCurrentUserBusinessOwner}
      />

    </div>
  );
};

export default ProfessionalCard; 